import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Box, IconButton, Typography } from '@mui/material';
import { InfoFilledIcon, StarFilledIcon, StarIcon } from '@sharedCR/coreDesignSystem/icons';
import { getRelativeTime } from 'app/pages/pushNotifications/pushNotifications.utils';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import {
  LabelSize,
  LabelVariant,
} from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { PushNotificationSourceTypeLabel } from 'constants/pushNotifications';
import { COLOR_PALETTE } from 'constants/ui';

import {
  LEARN_MORE_LABEL,
  SOURCE_ENTITY_PATH_BUILDERS,
  TYPE_ICON_MAP,
} from './PushNotificationCard.constants';
import {
  cardStyles,
  contentStyles,
  getStarButtonStyles,
  iconWrapperStyles,
  learnMoreLinkStyles,
  messageStyles,
  metaStyles,
  sourceEntityLinkStyles,
  timestampStyles,
  unreadDotStyles,
} from './PushNotificationCard.styles';
import { PushNotificationCardTestIds } from './PushNotificationCard.testIds';

const PushNotificationCard = ({
  notification,
  onCardClick = _.noop,
  onClose = _.noop,
  onToggleImportant = _.noop,
  isPreview = false,
}) => {
  const { type, source, message, detail, readDate, createdDate, isImportant } = notification;

  const isUnread = _.isNil(readDate);
  const IconComponent = TYPE_ICON_MAP[type] ?? InfoFilledIcon;
  const sourceLabel = PushNotificationSourceTypeLabel[source?.type];

  const iconWrapperSx = useMemo(
    () => ({ ...iconWrapperStyles, color: ({ palette }) => palette[type?.toLowerCase()]?.main }),
    [type],
  );

  const onClick = useCallback(() => {
    onCardClick(notification);
  }, [notification, onCardClick]);

  const onKeyDown = useCallback(
    e => {
      if (e.target !== e.currentTarget) {
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const onStarClick = useCallback(() => {
    onToggleImportant(notification);
  }, [notification, onToggleImportant]);

  const sourceEntityLinkTo = useMemo(() => {
    const buildPath = SOURCE_ENTITY_PATH_BUILDERS[source?.type];

    if (!buildPath || _.isNil(source?.sourceEntityId)) {
      return null;
    }

    return buildPath(source.sourceEntityId);
  }, [source?.type, source?.sourceEntityId]);

  const onSourceEntityLinkClick = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Box
      aria-label={message}
      data-id={notification.id}
      data-testid={PushNotificationCardTestIds.getCardId(notification.id)}
      role="button"
      sx={cardStyles}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {isUnread && !isPreview && <Box sx={unreadDotStyles} />}

      <Box sx={iconWrapperSx}>
        <IconComponent size={IconSize[20]} />
      </Box>

      <Box sx={contentStyles}>
        <Typography sx={messageStyles} variant="medium14">
          {message}
        </Typography>

        <Box sx={metaStyles}>
          {sourceLabel && (
            <Box>
              <LabelCDS
                withBorder
                size={LabelSize.S}
                title={sourceLabel}
                variant={LabelVariant.GRAY}
              />
            </Box>
          )}

          {sourceEntityLinkTo && source?.sourceEntityName && (
            <Typography
              component={Link}
              data-testid={PushNotificationCardTestIds.getSourceEntityLinkId(notification.id)}
              sx={sourceEntityLinkStyles}
              to={sourceEntityLinkTo}
              variant="medium14"
              onClick={onSourceEntityLinkClick}
            >
              {source.sourceEntityName}
            </Typography>
          )}

          {detail && <Typography variant="regular12">{detail}</Typography>}

          {source?.linkUrl && (
            <Typography
              component="a"
              data-testid={PushNotificationCardTestIds.getLearnMoreLinkId(notification.id)}
              href={source.linkUrl}
              rel="noopener noreferrer"
              sx={learnMoreLinkStyles}
              target="_blank"
              variant="medium14"
            >
              {LEARN_MORE_LABEL}
            </Typography>
          )}

          <Typography color={COLOR_PALETTE.GRAY[6]} sx={timestampStyles} variant="medium12">
            {getRelativeTime(createdDate)}
          </Typography>
        </Box>
      </Box>

      {!isPreview && (
        <IconButton
          data-is-important={isImportant}
          data-testid={PushNotificationCardTestIds.getStarButtonId(notification.id)}
          sx={getStarButtonStyles(isImportant)}
          onClick={onStarClick}
        >
          {isImportant ? <StarFilledIcon size={IconSize[20]} /> : <StarIcon size={IconSize[20]} />}
        </IconButton>
      )}
    </Box>
  );
};

PushNotificationCard.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    createdDate: PropTypes.string.isRequired,
    source: PropTypes.shape({
      type: PropTypes.string,
      linkUrl: PropTypes.string,
      sourceEntityId: PropTypes.string,
      sourceEntityName: PropTypes.string,
    }),
    detail: PropTypes.string,
    readDate: PropTypes.string,
    isImportant: PropTypes.bool,
  }).isRequired,
  isPreview: PropTypes.bool,
  onCardClick: PropTypes.func,
  onClose: PropTypes.func,
  onToggleImportant: PropTypes.func,
};

export default PushNotificationCard;
